import {
  isAddressComponent,
  isAuditCheckboxComponent,
  isAuditTagComponent,
  isAuditTextComponent,
  isDateComponent,
  isDateRangeComponent,
  isEmailComponent,
  isEntityBatchComponent,
  isFileComponent,
  isGeolocationComponent,
  isNumberComponent,
  isPersonComponent,
  isRelatedCardComponent,
  isScheduledAutomationComponent,
  isSignatureComponent,
  isSubformComponent,
  isTagComponent,
  isTextComponent,
  isTimeTrackerComponent,
  isTodoComponent,
  isUrlComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';
import { AddressField } from './AddressField';
import { AuditCheckboxField } from './AuditCheckboxField';
import { AuditTagField } from './AuditTagField';
import { AuditTextField } from './AuditTextField';
import { DateField } from './DateField';
import { DateRangeField } from './DateRangeField';
import { EmailField } from './EmailField';
import { EntityBatchField } from './EntityBatchField';
import { Field } from './Field';
import { FileField } from './FileField';
import { GeolocationField } from './GeolocationField';
import { NumberField } from './NumberField';
import { PersonField } from './PersonField';
import { RelatedEntityField } from './RelatedEntityField';
import { ScheduledAutomationField } from './ScheduledAutomationField';
import { SignatureField } from './SignatureField';
import { SubformField } from './SubformField';
import { TagField } from './TagField';
import { TextField } from './TextField';
import { TimeTrackerField } from './TimeTrackerField';
import { TodoField } from './TodoField';
import { UrlField } from './UrlField';

type WorkflowEntityRefs = NonNullable<NonNullable<API.ListWorkflowEntitiesQuery['list']>['refs']>;

export interface FieldHydrationContext {
  refs?: WorkflowEntityRefs;
  currentUser?: User;
}

/**
 * Converts raw JSON data to a Field instance based on the component type.
 *
 * Pass a {@link FieldHydrationContext} to hydrate ref-dependent fields
 * (Person, Tag, RelatedCard, Subform, EntityBatch) with resolved references.
 * Without context these fields still parse but carry no ref data.
 *
 * Does not handle entity-level derivations (RelatedCardLookup, CombinedTags,
 * DateRange from entity fields) — those require the full entity object.
 */
export function fieldFromComponentData(
  component: API.AnyComponent,
  data: any,
  ctx?: FieldHydrationContext,
): Field | null {
  if (isAuditTagComponent(component)) {
    return AuditTagField.fromFieldJson(data);
  }
  if (isAuditTextComponent(component)) {
    return AuditTextField.fromFieldJson(data);
  }
  if (isAuditCheckboxComponent(component)) {
    return AuditCheckboxField.fromFieldJson(data);
  }
  if (isAddressComponent(component)) {
    return AddressField.fromFieldJson(data);
  }
  if (isDateComponent(component)) {
    return DateField.fromFieldJson(data);
  }
  if (isDateRangeComponent(component)) {
    return DateRangeField.fromFieldJson(data);
  }
  if (isEmailComponent(component)) {
    return EmailField.fromFieldJson(data);
  }
  if (isFileComponent(component)) {
    return FileField.fromFieldJson(data);
  }
  if (isGeolocationComponent(component)) {
    return GeolocationField.fromFieldJson(data);
  }
  if (isNumberComponent(component)) {
    return NumberField.fromFieldJson(data, component.format);
  }
  if (isScheduledAutomationComponent(component)) {
    return ScheduledAutomationField.fromFieldJson(data);
  }
  if (isSignatureComponent(component)) {
    return SignatureField.fromFieldJson(data);
  }
  if (isTimeTrackerComponent(component)) {
    return TimeTrackerField.fromFieldJson(data);
  }
  if (isTodoComponent(component)) {
    return TodoField.fromFieldJson(data);
  }
  if (isUrlComponent(component)) {
    return UrlField.fromFieldJson(data);
  }
  if (isPersonComponent(component)) {
    return PersonField.fromFieldJson(data, ctx?.refs?.personRefs ?? null, ctx?.currentUser);
  }
  if (isTagComponent(component) && component.options) {
    return TagField.fromFieldJson(
      data,
      component.id,
      component.options,
      component.multiselect ?? false,
      component.addAdditionalValues ?? false,
    );
  }
  if (isEntityBatchComponent(component)) {
    return EntityBatchField.fromFieldJson(data, ctx?.refs?.recurringEntitiesScheduleRefs);
  }
  if (isRelatedCardComponent(component)) {
    return RelatedEntityField.fromFieldJson(
      data,
      ctx?.refs?.workflowEntityRefs,
      ctx?.refs?.workflowTemplateRefs,
    );
  }
  if (isSubformComponent(component)) {
    return SubformField.fromFieldJson(data, ctx?.refs?.workflowTemplateRefs);
  }
  if (isTextComponent(component)) {
    return TextField.fromFieldJson(data, { multiline: component.multiline });
  }
  return null;
}
